import React, { useState } from "react";
import { BrowserRouter as Router, Route, Switch, Link, useLocation } from "react-router-dom";
import { AppBar, Toolbar, IconButton, Typography, Container, Avatar, Box, Menu, MenuItem, CssBaseline, ThemeProvider, createTheme, } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Settings } from "@mui/icons-material";

// Styles
import './stylesheet.css';
import './toggleSwitch.css';
import './App.css';
import './ButtonStyle.scss';
import './loding.scss';

// Auth
import { AuthProvider, useAuth } from "./AuthContext";

// Error Handling
import { ErrorProvider, useError } from "./contexts/ErrorContext";
import ErrorAnimation from "./components/ErrorAnimation";
import ErrorBoundary from "./components/ErrorBoundary";
import { initializeErrorHandling } from "./apiService";

// Theme
import { ThemeProvider as CustomThemeProvider } from "./contexts/ThemeContext";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";

// Sidebar + Logout
import Sidebar from "./Sidebar"; // move Sidebar into its own file for clarity
import Logout from "./Logout";

// Pages
//----------------Company Pages------------ //
import CompanyEdit from "./components/pages/Company/Edit";
//----------------Home Pages--------------- //
import HomePage from "./components/pages/Home";
//---------------- User Pages ------------- //
import UserIndex from "./components/pages/User/Index";
import UserCreate from "./components/pages/User/Create";
import UserEdit from "./components/pages/User/Edit";
import UserDetail from "./components/pages/User/Detail";
import PasswordChange from "./components/pages/User/PasswordChange";
//----------------Role Pages--------------- //
import RoleIndex from "./components/pages/Role/Index";
import RoleDetail from "./components/pages/Role/Detail";
//----------------Category Pages-------- //
import CategoryIndex from "./components/pages/Category/Index";
//----------------Item Pages--------------- //
import ItemIndex from "./components/pages/Item/Index";
//---------------IncomeExpenseHead Pages--- //
import IncomeExpenseHeadIndex from "./components/pages/IncomeExpenseHead/Index";
//----------------Customer Pages----------- //
import CustomerIndex from "./components/pages/Customer/Index";
//----------------Supplier Pages-------- //
import SupplierIndex from "./components/pages/Supplier/Index";
//----------------Invoice Pages------------ //
import InvoiceIndex from "./components/pages/Invoice/Index";
import InvoiceGenerate from "./components/pages/Invoice/InvoiceGenerate";
//----------------Receipt Pages------------ //
import PaymentAndReceiptCreate from "./components/pages/PaymentAndReceipts/Create";
import PaymentAndReceiptIndex from "./components/pages/PaymentAndReceipts/Index";
import PaymentAndReceiptDetails from "./components/pages/PaymentAndReceipts/Details";
import PaymentAndReceiptEdit from "./components/pages/PaymentAndReceipts/Edit";
//-----------------Report------------------//
import Ledger from "./components/pages/Reports/Ledger";
import Outstanding from "./components/pages/Reports/outstanding";
import ExpenseReport from "./components/pages/Reports/ExpenseReport";
import InvoiceReport from "./components/pages/Reports/InvoiceReport";
import DetailedInvoice from "./components/pages/Reports/InvoiceDetailsReport";
//-----------------Report------------------//
import PurchaseIndex from "./components/pages/purchase/Index";
import PurchaseCreate from "./components/pages/purchase/Create";
import PurchaseDetails from "./components/pages/purchase/Details";
import PurchaseEdit from "./components/pages/purchase/Edit";
//----------------Food Register Pages------ //
import DailyFoodRegister from "./components/pages/FoodRegister/DailyFoodRegister";
//----------------Bank Register Pages------ //
import BankIndex from "./components/pages/Bank/Index";
import BankCreate from "./components/pages/Bank/Create";
import BankEdit from "./components/pages/Bank/Edit";
//----------------Income Expense Entry Pages------ //
import IncomeExpenseCreate from "./components/pages/IncomeExpense/Create";
import IncomeExpenseDetails from "./components/pages/IncomeExpense/Details";
import IncomeExpenseEdit from "./components/pages/IncomeExpense/Edit";
import IncomeExpenseIndex from "./components/pages/IncomeExpense/Index";

// ---------------- Theme ---------------- //
const theme = createTheme({
  palette: {
    // primary: { main: "#2F5249" },
    // secondary: { main: "#4CAF50" },
    primary: { main: "#173B45" },
    secondary: { main: "#173B45" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
        },
      },
    },
  },
});

// ---------------- Layout ---------------- //
const Layout = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/Home';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false); // sidebar hover state
  const [anchorEl, setAnchorEl] = useState(null);
  const { companyName, roleName, username } = useAuth();

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Header */}
      <AppBar position="fixed" color="primary" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }} style={{ height: '55px' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{ flexGrow: 1, ml: 2, fontWeight: "bold", textTransform: "uppercase" }}
          >
            <Link to="/Home" style={{ textDecoration: "none", color: "inherit" }}>
              <span style={{ fontFamily: 'Verdana, sans-serif', fontSize: '14px' }}>{companyName}</span>
            </Link>
          </Typography>

          <IconButton color="inherit" onClick={handleMenu}>
            <Avatar sx={{ bgcolor: "#4CAF50", width: 32, height: 32 }}>
              {username?.[0]}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem onClick={handleClose}>
              <Settings fontSize="small" sx={{ mr: 1 }} /> Settings
            </MenuItem>
            <Logout asMenuItem onAfterLogout={handleClose} />
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Sidebar roleName={roleName} />

      {/* Content */}
      <Box
        sx={{
          ml: isHomePage ? 0 : { xs: 0, md: "70px" },
          pt: 10,
        }}
      >
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Switch>
            <Route path="/" exact component={HomePage} />
            <Route path="/Home" exact component={HomePage} />
            <Route path="/RoleIndex" component={RoleIndex} />
            <Route path="/RoleDetail/:id" component={RoleDetail} />
            <Route path="/UserIndex" component={UserIndex} />
            <Route path="/UserCreate" component={UserCreate} />
            <Route path="/UserEdit/:id" component={UserEdit} />
            <Route path="/UserDetail/:id" component={UserDetail} />
            <Route path="/PasswordChange" component={PasswordChange} />
            <Route path="/CustomerIndex" component={CustomerIndex} />
            <Route path="/BankIndex" component={BankIndex} />
            <Route path="/CompanyEdit" component={CompanyEdit} />
            <Route path="/CategoryIndex" component={CategoryIndex} />
            <Route path="/ItemIndex" component={ItemIndex} />
            <Route path="/SupplierIndex" component={SupplierIndex} />
            <Route path="/IncomeExpenseHeadIndex" component={IncomeExpenseHeadIndex} />
            <Route path="/InvoiceIndex" component={InvoiceIndex} />
            <Route path="/InvoiceGenerate" component={InvoiceGenerate} />
            <Route path="/PaymentAndReceiptCreate" component={PaymentAndReceiptCreate} />
            <Route path="/PaymentAndReceiptIndex" component={PaymentAndReceiptIndex} />
            <Route path="/PaymentAndReceiptDetails/:id" component={PaymentAndReceiptDetails} />
            <Route path="/PaymentAndReceiptEdit/:id" component={PaymentAndReceiptEdit} />
            <Route path="/Ledger" component={Ledger} />
            <Route path="/Outstanding" component={Outstanding} />
            <Route path="/ExpenseReport" component={ExpenseReport} />
            <Route path="/InvoiceReport" component={InvoiceReport} />
            <Route path="/DetailedInvoice" component={DetailedInvoice} />

            {/* Purchase */}
            <Route path="/PurchaseIndex" component={PurchaseIndex} />
            <Route path="/PurchaseCreate" component={PurchaseCreate} />
            <Route path="/PurchaseDetails/:id" component={PurchaseDetails} />
            <Route path="/PurchaseEdit/:id" component={PurchaseEdit} />

            {/* Food Register */}
            <Route path="/DailyFoodRegister" component={DailyFoodRegister} />

            {/* Bank */}
            <Route path="/BankIndex" component={BankIndex} />
            <Route path="/BankCreate" component={BankCreate} />
            <Route path="/BankEdit/:id" component={BankEdit} />

            {/* Income & Expense Entry */}
            <Route path="/IncomeExpenseIndex" component={IncomeExpenseIndex} />
            <Route path="/IncomeExpenseCreate" component={IncomeExpenseCreate} />
            <Route path="/IncomeExpenseDetails/:id" component={IncomeExpenseDetails} />
            <Route path="/IncomeExpenseEdit/:id" component={IncomeExpenseEdit} />
          </Switch>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

// ---------------- Root ---------------- //
const App = () => {
  return (
    <Router>
      <ErrorProvider>
        <ErrorInterceptorInitializer />
        <ErrorBoundary>
          <AuthProvider>
            <CustomThemeProvider>
              <UserPreferencesProvider>
                <Layout />
                <ErrorOverlay />
              </UserPreferencesProvider>
            </CustomThemeProvider>
          </AuthProvider>
        </ErrorBoundary>
      </ErrorProvider>
    </Router>
  );
};

// ---------------- Error Overlay Component ---------------- //
const ErrorOverlay = () => {
  const { error, clearError, retryLastRequest } = useError();

  return (
    <ErrorAnimation
      error={error}
      onRetry={retryLastRequest}
      onDismiss={clearError}
    />
  );
};

// ---------------- Error Interceptor Initializer ---------------- //
const ErrorInterceptorInitializer = () => {
  const errorContext = useError();

  React.useEffect(() => {
    // Initialize error handling when component mounts
    const cleanup = initializeErrorHandling(errorContext);

    // Cleanup on unmount
    return cleanup;
  }, [errorContext]);

  return null; // This component doesn't render anything
};

export default App;
